import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';

/**
 * @entity DuplicateDataTracker
 * This class represents a possible duplicate in the database and give some functionality to resolve the conflict
 */
@Entity()
export class DuplicateDataTracker extends MongoEntity {
  public static override _class = 'DuplicateDataTracker';
  public static override _collectionName = 'duplicates';

  @Field(type => DateTime, {
    default: v => v ?? DateTime.now(),
  })
  public createdAt: Maybe<DateTime>;

  @Field(type => ID)
  public entityID: Maybe<string>;

  @Field(type => String)
  public entityCollection: Maybe<string>;

  @Field(type => MongoEntity, {
    resolve: function (this: DuplicateDataTracker) {
      return async () => {
        if (!this.entityID || !this.entityCollection) return null;
        const collection = await getRawCollection<EntityData<MongoEntity>>(this.entityCollection);
        return constructValue(await collection.findOne({ _id: this.entityID }));
      };
    },
  })
  public entity: Maybe<MongoEntity>;

  @Field(type => ID)
  public duplicateID: Maybe<string>;

  @Field(type => String)
  public duplicateCollection: Maybe<string>;

  @Field(type => MongoEntity, {
    resolve: function (this: DuplicateDataTracker) {
      return async () => {
        if (!this.duplicateID || !this.duplicateCollection) return null;
        const collection = await getRawCollection<EntityData<DuplicateDataTracker>>(this.duplicateCollection);
        return constructValue(await collection.findOne({ _id: this.duplicateID }));
      };
    },
  })
  public duplicate: Maybe<MongoEntity>;

  @Field(type => Float, {
    default: v => v ?? 0,
  })
  public probabilityOfDuplicate: Maybe<number>;

  @Field(type => Boolean)
  public blacklisted: Maybe<boolean>;

  @Mutation(type => Int)
  public static searchForDuplicates() {
    return async (gqlFields: void) => {
      const procedures = await import('@chancenwerk/server/searchForDuplicates.js');

      // Dont await this. Its an asynchronous task on the server. The Client subscribes the results
      return procedures.searchForDuplicates();
    };
  }

  @Mutation(type => [DuplicateDataTracker])
  public static searchForDuplicateOfUserData(@Arg('user', type => ChancenwerkUser) user: ChancenwerkUser) {
    return async (gqlFields: BaseQLSelectionSet<DuplicateDataTracker>) => {
      const procedures = await import('@chancenwerk/server/searchForDuplicates.js');
      const duplicateTracker = await procedures.searchForDuplicateForUserData(user);
      return duplicateTracker.sort((a, b) => (b.probabilityOfDuplicate ?? 0) - (a.probabilityOfDuplicate ?? 0));
    };
  }

  @Subscription(type => [DuplicateDataTracker])
  static subscribeDuplicatedPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
  ) {
    return (gqlFields: BaseQLSelectionSet<DuplicateDataTracker>) => {
      const selector = { blacklisted: false };
      const handle = getCollectionHandle<DuplicateDataTracker>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { probabilityOfDuplicate: -1 },
          });
        }),
        catchError(err => {
          logger.error({ err }, 'Error during subscription');
          return of([] as DuplicateDataTracker[]);
        }),
      );
    };
  }

  @Mutation(type => Boolean)
  public static deleteMergedDuplicateTracker(@Arg('_id', type => String) _id: string) {
    return async () => {
      // the duplicate record is now set to itself because of the merging. So we can just search for both fields being the same
      const coll = await getRawCollection<EntityData<DuplicateDataTracker>>(this._collectionName);
      const result = await coll.deleteOne({
        entityID: _id,
        duplicateID: _id,
      });

      return result.deletedCount > 0;
    };
  }
}
